import React, { useContext, useEffect, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import Chart from "core/ui/charts/index";
import FormatDate from "core/utils/FormatDate";
import FormatDateTime from "core/utils/FormatDateTime";
import SessionContext from "SessionContext";
import { formatNumber } from "../core/ui/charts/chart-base";
import RocAucModelGauge from "../core/ui/roc-auc-model-gauge";
import ComplexFilterBuilder from "./complex-filter-builder";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Card, CardHeader, CardBody, Label, Alert, PopoverBody, PopoverHeader, Popover, Badge } from "reactstrap";
import TreeView, { flattenTree } from "react-accessible-treeview";
import { scoringPartitionOptions } from "./model";
import ConfirmSuccess from "../core/ui/confirm-success";
import EditNameAndDescription from "../partials/edit-name-description";
import isEqual from "lodash.isequal";
import { existingComplexFilters, existingSimpleFilters } from "../core/utils/data-helper-functions";

export default function ModelDetails(props) {
  const session = useContext(SessionContext);
  const access = session.appState.userCan;

  const history = useHistory();
  const [model, setModel] = useState();
  const [selectedChart, setSelectedChart] = useState(0);
  const [selectedTreeNode, setSelectedTreeNode] = useState([2]);
  const [expandedTreeNodes] = useState([1]);
  const [activeFilters, setActiveFilters] = useState();
  const [allFields] = useState(Object.values(session.chartsStore.modelable));
  const [excludedFields, setExcludedFields] = useState([]);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [reloadingToggle, setReloadingToggle] = useState();
  const [refreshing, setRefreshing] = useState(false);
  const scoringPartitionLookup = Object.fromEntries(scoringPartitionOptions);
  const [isComplex, setIsComplex] = useState();
  const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
  const [complexFilters, setComplexFilters] = useState();
  const [complexFields, setComplexFields] = useState();
  const [editNameModal, setEditNameModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [positiveRecordsInfoOpen, setPositiveRecordsInfoOpen] = useState(false);
  const [savedSelectionList, setSavedSelectionList] = useState();
  // eslint-disable-next-line
  const [dataFieldsHaveChanged, setDataFieldsHaveChanged] = useState(false);
  const [dataFiltersHaveChanged, setDataFiltersHaveChanged] = useState(false);
  const [noFilters, setNoFilters] = useState(false);
  const [missingFiltersModalIsOpen, setMissingFiltersModalIsOpen] = useState(false);
  const [showEfficacyWarning, setShowEfficacyWarning] = useState(false);
  const [showEfficacyError, setShowEfficacyError] = useState(false);
  const [showLowEfficacyError, setShowLowEfficacyError] = useState(false);

  const formatChartName = (_val) => {
    // replace "_n_" with " And " and underscores with spaces
    let formatted = _val.replace(/_n_/g, " And ").replace(/_/g, " ");
    
    // capitalize each word
    formatted = formatted.replace(/\b\w/g, (char) => char.toUpperCase());

    // add a dash between number ranges
    formatted = formatted.replace(/(\d+)\s+(\d+)/g, "$1 - $2");
    
    return formatted;
  }

  const getFeatureChartNames = () => {
    if(model && model.charts) {
      const _features = Object.entries(model.charts)
      .filter(([key]) => key.startsWith('feature')) // only use if starts with 'feature'
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB, undefined, { numeric: true })) // sort by the number in 'feature'
      .map(([, value]) => ({ name: formatChartName(value.title) })); 
      return _features;
    }
    return [];
  }
  
  // Nav Tree Data
  const folder = {
  name: "",
  children: [
    {
      name: "Stats",
      children: [
        {
          name: "Prediction Density",
        },
        {
          name: "ROC Curve"
        },
      ]
    },
    {
      name: "Features",
      children: [
        {
          name: "Feature Importance",
        },
        { name: "Feature Importance (Detailed)" },
        {
          name: "Individual Feature Details",
          children: getFeatureChartNames(),
        },
      ],
    },
  ],
  };
  
  // Helper function to create Nav Tree from Object
  const data = flattenTree(folder);

  const getChartUrls = () => {
    let idCounter = 7;
    if(model && model.charts) {
      // start with fixed menu items
      let _features = [
        {id: 1, src: "summary_2"},
        {id: 2, src: "summary_4"},
        {id: 3, src: "summary_4"},
        {id: 4, src: "summary_1"},
        {id: 5, src: "summary_3"},
        {id: 6, src: "summary_3"},
      ];
      // add dynamic feature items
      for (let i = 1; i <= 20; i++) {
        let nextFeature = `feature_${i}`;
        if(model.charts[nextFeature]) {
          _features.push( {id: idCounter++, src: nextFeature} )
        }
      }
      return _features;
    } 
    return [];
  }

  // Chart Image Data for Nav Tree
  const chartData = getChartUrls();

  useEffect(() => {
    if (model) {

      // If charts are loaded for the wrong population domain, skip entirely —
      // comparing against wrong-domain charts would flag valid filters as missing.
      if (model?.population_domain) {
        const loadedDomain = session.crossfilterStore.explorerModule || "consumers";
        if (model.population_domain !== loadedDomain || !session.chartsStore.loaded || !session.chartsStore.charts) {
          return
        };
      }

      let availableFilters;
      if (model.filters || model.complex_filters) {
        if (model.status === "Building") {
          setButtonsDisabled(true);
          setReloadingToggle(true);
        } else {
          setRefreshing(false);
          setButtonsDisabled(false);
        }
        if (model.is_complex) {
          const updatedFilters = checkForExistingFilters();
          setComplexFilters(updatedFilters);
          setIsComplex(model.is_complex);
          session.crossfilterStore.getComplexFields().then((_complexFields) => {
            setComplexFields(_complexFields);
          });
        } else {
          "saved_selection_criteria" in model && setSavedSelectionList(model.saved_selection_criteria);
          availableFilters = checkForExistingFilters();
          setActiveFilters(
            availableFilters
            .filter(([chart_id, filters]) => !!filters)
            .map(([chart_id, filters]) => {
              return [chart_id, filters];
            })
          );
        }
      }
      // check if selected data fields are still available
      if (model && model.excluded_fields && model.excluded_fields.length) {
          setDataFieldsHaveChanged(checkDataFieldsExist(model.excluded_fields));
      }

      // set excluded fields to what are still available
      const availableFields =
          allFields.filter((a) =>
              (model.excluded_fields || []).find((f) => a.ds === f.ds && a.id === f.id )
          )

      availableFields && setExcludedFields(availableFields);

      setSelectedTreeNode([2]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, session.chartsStore.loaded]);

  useEffect(() => {
    if (props.args.model) {
      loadModel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sleep = (milliseconds) => {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  };

  useEffect(() => {
    if (typeof reloadingToggle !== "undefined") {
      if (props.args.model) {
        session
          .query("model/get", { id: props.args.model })
          .then((response) => {
            if (response.status === "Building") {
              sleep(2000).then((r) => {
                //toggle reloadingToggle to trigger ourselves
                setReloadingToggle(!reloadingToggle);
              });
            } else {
              loadModel();
            }
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadingToggle]);

    // Check that data fields that are selected still exist.
    function checkDataFieldsExist(_fields) {
        if (!_fields.length) return true;
        let fieldExists;
        fieldExists = allFields.filter((a) => (_fields || []).find((f) => (a.ds === f.ds && a.id === f.id)));
        const missingFields = (fieldExists.length !== _fields.length);
        return missingFields;
  }

  // Check saved filters against existing filters
  function checkForExistingFilters() {
    if (!model) return;
    let missing = false;
    let availableFilters;
    if (model.is_complex) {
      const charts = session.chartsStore.charts;
      availableFilters = JSON.parse(JSON.stringify(model.complex_filters));
      availableFilters = existingComplexFilters(model.complex_filters, charts, availableFilters);
      missing = !isEqual(availableFilters.tree.children1, model.complex_filters.tree.children1);
      const no_filters = availableFilters && !Object.keys(availableFilters.tree.children1).length;
      setNoFilters(no_filters);
    } else {
      const charts = Object.entries(session.chartsStore.charts);
      availableFilters = JSON.parse(JSON.stringify(model.filters));
      availableFilters = existingSimpleFilters(model.filters, charts, availableFilters);
      missing = !isEqual(availableFilters, Object.entries(model.filters));
      setNoFilters(availableFilters && !availableFilters.length);
    }
    setDataFiltersHaveChanged(missing);
    return availableFilters;
  }

  function loadModel() {
    session.query("model/get", { id: props.args.model }).then((model) => {
      setModel(model);
    });
  }

  function onEdit() {
    session
      .command("model/revert_to_creating", { id: props.args.model })
      .then((_model) => {
        if (_model.status === "Created") {
          history.push(`/model#model=${props.args.model}`);
        }
      });
  }

  const toggleShowEfficacyWarning = () => {
    setShowEfficacyWarning(!showEfficacyWarning);
  }

  const toggleShowEfficacyError = () => {
    setShowEfficacyError(!showEfficacyError);
  }

  const toggleShowLowEfficacyError = () => {
    setShowLowEfficacyError(!showLowEfficacyError);
  }

  function onClickScoreHandler(){
    let high_warning = window.model_high_warning_value;
    let high_error = window.model_high_error_value;
    let low_error = window.model_low_error_value;
    let roc_auc = model.roc_auc;
    
    if(roc_auc <= low_error) {
      setShowLowEfficacyError(true);
    } else if(roc_auc >= high_error) {
      setShowEfficacyError(true);
    } else if(roc_auc >= high_warning) {
      setShowEfficacyWarning(true);
    } else {
      handleSubmitScore();
    }    
  }

  const handleSubmitScore = () => {
    if (model.status === "Built" && (dataFiltersHaveChanged || noFilters)) {
      setShowEfficacyWarning(false);
      setMissingFiltersModalIsOpen(true);
    } else {
      setShowEfficacyWarning(false);
      onScore();
    }
  }

  function onScore(ev) {
    session.crossfilterStore.score_model(
      { id: props.args.model },
      (response) => {
        history.push(`/models`);
      },
      (error) => {
        console.log(`Error: ${JSON.stringify(error)}`);
      }
    );
  }

  function toggleModal(event) {
    event.preventDefault();
    setErrorModalIsOpen(!errorModalIsOpen);
  }

  const selectedCriteriaBuilder = () => {
      if (!savedSelectionList) {
        return "";
      }
      const charts = Object.entries(session.chartsStore.charts);
      let availableFilters = JSON.parse(JSON.stringify(model.filters));
      availableFilters = existingSimpleFilters(model.filters, charts, availableFilters);
  
      let savedFeatureIds = [];
      let savedFeatureNames = [];
      let availableFeatureIds = [];
      let availableFeatureNames = [];
      savedSelectionList.forEach((feature) => {
        feature.id && savedFeatureIds.push(feature.id);
        feature.filter_name && savedFeatureNames.push(feature.filter_name);
      });
      availableFilters.forEach((feature) => {
        feature[0] && availableFeatureIds.push(feature[0]);
        feature[1].filter_name && availableFeatureNames.push(feature[1].filter_name);
      });
      
      savedSelectionList.forEach(feature => { 
        if (savedFeatureIds.length && availableFeatureIds.length && feature.id) {
          feature.missing = !availableFeatureIds.includes(feature.id);
        } else if(savedFeatureNames.length && availableFeatureNames.length && feature.filter_name) {
          feature.missing = !availableFeatureNames.includes(feature.filter_name);
        }
      });
  
      // format value_labels into an array for display
      const criteria_list = savedSelectionList.map(item => {
        if (!item.filter_values) return null;
        return {
          ...item,
          filter_values: Array.isArray(item.filter_values)
            ? item.filter_values
            : Object.values(item.filter_values)
        };
      }).filter(Boolean);

    return (
      criteria_list.map((val) => {
        if(!val) {return ""}
        return (
          <div key={val.filter_name}>
            <div className="view-only card">
              {val.missing &&
            <div className="card-header">
                  <div className="missing-feature">
                    <Badge color="warning"><i className="bi bi-exclamation-triangle-fill"></i> Feature no longer available</Badge>
                  </div>
            </div>
          }
            <div className="chart-card card-body">
            <figure className="chart-widget is-view-only pie-chart">
            <figcaption className="heading" draggable="false">
            <span>
            <span className="hovertext" data-hover={val.filter_name}>
              {val.is_excluded && "Excluded:"} {val.filter_name}
            </span>
            </span>
            <i className="spacer"></i>
            <div className="actions"></div>
            </figcaption><div className="summary">
            <ul>
              {val && val.filter_values && val.filter_values.map((item) => {
                return <li key={item.value || item}>{item.value || item}
                {item.match_value && <span className='match-level-details'><span className='label'>Match Level: </span><span className='value'>{item.match_value}</span></span>}
              </li>
              })
              }
            </ul>
            </div>
            </figure>
            </div>
            </div>
          </div>
        )
      }
      )
    )
  };

  if (!model) return <div></div>;

  function findChartView(id) {
    if(!id || !model.charts) {return}
    const charts = model.charts;
    let selectedChart = "";
    chartData.forEach((chart) => {
      if(chart.id === id) {
        selectedChart = charts[chart.src].url;
      }
    });
    return selectedChart;
  }
  
  function handleMenuSelect(e) {
    if (!e.isSelected) { return; }
    if (e.isBranch) {
      // select children[0]
      setSelectedTreeNode([e.element.children[0]]);
      return;
    }
    const id = e.element.id - 1;
    const selectedView = findChartView(id);
    setSelectedChart(selectedView);
  }

  function toggleEditNameModal() {
    setEditNameModal(!editNameModal);
  }

  function onUpdateName(_name, _description) {
    let updatedModel = {...model, name: _name, description: _description}
    session.command("model/update",
      updatedModel, {}, { defaultErrorHandling: false }
    )
    .then((res) => {
      toggleEditNameModal();
      setShowSuccess(true);
      setTimeout(() => {
        history.push("/models");
      }, 2000);
    })
    .catch((error) => {
      console.log("Failed to update model", error)
    })
  }

  const showPositiveRecordsInfo = () => { 
    setPositiveRecordsInfoOpen(!positiveRecordsInfoOpen); 
  }

  return (
    <div className="page">
      <div className="model-header list-header sticky-page-header">
        <Link to="/models">
          <button type="button" className="new-button">
            <i className="bi bi-chevron-left"></i> Models
          </button>
        </Link>
        <h1>Model Details & Training Data</h1>
        <i className="spacer"></i>
        {
          model.status !== "Scoring" &&
          model.status !== "Scored" &&
          (
            <div className="model-actions header-actions">
              {access('model_edit') &&
                <Button
                  className="new-buttom"
                  size="sm"
                  type="button"
                  onClick={onEdit}
                  disabled={buttonsDisabled}
                >
                  Edit Model
                </Button>
              }
              {
                (model.status === 'Built') && access('model_edit') && (
                  <Button
                    className="new-buttom"
                    size="sm"
                    type="button"
                    onClick={toggleEditNameModal}
                  >Edit Name/Description</Button>
                )
              }
              {access('model_score') &&
                <Button
                  className="new-buttom promote"
                  size="sm"
                  type="button"
                  onClick={onClickScoreHandler}
                  disabled={buttonsDisabled}
                >
                  Score Universe
                </Button>
              }
              </div>
          )}
          {
            (model.status === 'Scored') && access('model_edit') && (
              <Button
                className="new-buttom"
                size="sm"
                type="button"
                onClick={toggleEditNameModal}
              >Edit Name/Description</Button>
            )
          }
      </div>
    
      <div className="model-campaign-page">
        {refreshing && (
          <div className="overlay">
            <div className="loader" />
          </div>
        )}
        <section id="place-model" className="main-content">
          <div id="model">
            {model.status === "Building" && (
              <>
                <div id="building" className="mt-4">
                  <h3>Model Currently Building</h3>
                </div>
              </>
            )}
            {model.status === "Error" && (
              <>
                <div id="building" className="mt-4">
                  <h3>Error Building Model</h3>
                </div>
              </>
            )}
            {model.charts &&
              (model.status === "Built" ||
                model.status === "Scoring" ||
                model.status === "Scored") && (
                <>
                <Card className="model-charts">
                  <CardHeader>
                    <div id="model-summary">
                      <div className="summary-items">
                        <div className="labeled-items horizontal">
                          <div className="labeled-item primary">
                            <Label>Model Name:</Label>
                            <span className="value">{model.name}</span>
                          </div>
                          <i className="spacer"></i>
                          <div className="labeled-item">
                            <Label>Model Created By:</Label>
                            <span className="value">{model.created_by}</span>
                          </div>
                            <div className="labeled-item">
                            <Label>Model Creation Date:</Label>
                            <span className="value">
                              <FormatDate date={model.created_date} />
                            </span>
                          </div>
                          <div className="labeled-item description">
                            <Label>Description:</Label>
                            <span className="value">{model.description}</span>
                          </div>
                          <div className="labeled-item staus">
                            <Label>Status:</Label>
                            <span className="value">{model.status}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody className="model-nav-sidebar">
                    <div className="model-nav">
                      <TreeView
                        data={data}
                        className="model"
                        aria-label="basic example tree"
                        onSelect={handleMenuSelect}
                        selectedIds={selectedTreeNode}
                        expandedIds={expandedTreeNodes}
                        nodeRenderer={({ element, getNodeProps, isBranch, level, handleSelect }) => (
                          <div {...getNodeProps()} style={{ paddingLeft: 12 * (level - 1) }}>
                            <label>{element.name}</label>
                            {isBranch && <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-caret-right-fill" viewBox="0 0 16 16">
  <path d="m12.14 8.753-5.482 4.796c-.646.566-1.658.106-1.658-.753V3.204a1 1 0 0 1 1.659-.753l5.48 4.796a1 1 0 0 1 0 1.506z"/>
</svg>}
                          </div>
                        )}
                        />
                    </div>
                    <div className="model-charts">
                      <div className="model-chart">
                        <img
                          alt={selectedChart}
                          src={selectedChart}
                          align="center"
                          />
                      </div>
                    </div>
                    </CardBody>
                  </Card>
                </>
              )}
          </div>
        </section>
        <section id="right-sidebar" className="model-overview">
          <div id="audience-summary">
            {model.roc_auc
              &&
              <Card>
                <CardBody>
                  <RocAucModelGauge roc_auc={model.roc_auc} />
                </CardBody>
              </Card>}
            <h2>Audience Summary</h2>
            <div id="summary">
              <div id="criteria">
                <h3>Selection Criteria</h3>
                <Alert
                      isOpen={dataFiltersHaveChanged || noFilters}
                      color="warning"
                      className="d-flex"
                      >
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      <div>
                          Since the last time this model was built or scored, one or more of the filters chosen are no longer available. <strong>Please review the selection criteria below prior to building, scoring or using your model in another target audience.</strong>.
                      </div>
                  </Alert>
                { savedSelectionList ? selectedCriteriaBuilder() :
                  activeFilters &&
                    activeFilters.map((filter) => (
                      <Chart
                        summarize
                        chart_id={filter[0]}
                        key={filter[0]}
                        filters={filter[1]}
                        viewOnly
                      />
                    ))
                  }
                {noFilters && <Card className="p-3">No selected criteria found.</Card>}
                {isComplex && !noFilters && (
                  <button onClick={toggleModal}>Complex Filters</button>
                )}
              </div>
              <div>
                <Modal
                  size="lg"
                  centered
                  backdrop
                  isOpen={errorModalIsOpen}
                  toggle={toggleModal}
                >
                  <ModalHeader toggle={toggleModal}>
                    Complex Filters
                  </ModalHeader>
                  <ModalBody>
                    <ComplexFilterBuilder
                      initialQuery={
                        isComplex && complexFilters && complexFilters.tree
                          ? complexFilters.tree
                          : null
                      }
                      fields={complexFields}
                      readonly={true}
                    />
                  </ModalBody>
                  <ModalFooter>
                    <Button onClick={toggleModal}>Close</Button>
                  </ModalFooter>
                </Modal>
                <Modal centered backdrop
                  isOpen={missingFiltersModalIsOpen}
              >
                  <ModalHeader>
                  <i className="bi bi-exclamation-triangle-fill"></i>
                      Filters Missing
                  </ModalHeader>
                  <ModalBody>
                      This model was built with one or more filters that are no longer available. Do you want to proceed with scoring?
                  </ModalBody>
                  <ModalFooter>
                    <Button onClick={(e) => setMissingFiltersModalIsOpen(false)}>Cancel</Button>
                      <Button className="promote" onClick={(e) => {onScore(e)}}>Continue</Button>
                  </ModalFooter>
              </Modal>
              </div>
               <h2>Model Summary</h2>
              <Card>
              <div className="labeled-items vertical">
                <div className="labeled-item">
                  <label>Restrictions:</label>
                  {model.restrict_geography && <span className="value">Input geography only</span>}
                  {model.filter_model_name && (
                    <span className="value">Saved Audience: {model.filter_model_name}</span>
                  )}
                  {!model.restrict_geography && !model.filter_model_name && <span className="value">None</span>}
                </div>
                <div className="labeled-item">
                  <label>Scoring Partitions:</label>
                  <span className="value">
                    {scoringPartitionLookup[model.scoring_quantiles]}
                    </span>
                  </div>
                  {excludedFields.length > 0 && (
                    <div className="labeled-item">
                      <label>Excluded Fields:</label>
                          {excludedFields.map((data) => (
                            <span className="value" key={data.label}>{data.label}</span>
                          ))}
                    </div>
                    )}
                {model.positive_count > -1 && (
                  <div className="labeled-item">
                    <label>Positive Records Available: <i className="bi bi-info-circle-fill pop-info" id="positive_records" /></label>
                    <div className="value">
                      {formatNumber(model.positive_count)}
                    </div>
                    {model.positive_count < 1000 && (
                      <Alert color="warning">
                        <div><i className="bi bi-exclamation-triangle-fill"></i>For best results, at least <strong>1000</strong> positive records are needed for training. This model currently has <strong>{model.positive_count}</strong>.</div>
                      </Alert>
                    )}
                    <Popover
                      flip
                      fade
                      trigger="hover"
                      onMouseOut={() => setPositiveRecordsInfoOpen(false)}
                      target="positive_records"
                      isOpen={positiveRecordsInfoOpen}
                      toggle={showPositiveRecordsInfo}
                    >
                      <PopoverHeader>Positive Records Available</PopoverHeader>
                      <PopoverBody>
                      "Positive Records Available" is the number of records that matched to the AudienceMaker prospect universe and can therefore be used for modeling.
                      </PopoverBody>
                    </Popover>
                  </div>
                )}
                {model.negative_count > -1 && (
                  <div className="labeled-item">
                    <label>Negative Records Available:</label>
                    <div className="value">
                      {formatNumber(model.negative_count)}
                    </div>
                  </div>
                )}
                {model.scoring_universe > -1 && (
                  <div className="labeled-item">
                    <label>Universe of Records</label>
                    <div className="value">
                      {formatNumber(model.scoring_universe)}
                    </div>
                  </div>
                )}
                </div>
              </Card>
              </div>
            <div className="sidebar-footer">
              {model.status === "Scoring" && (
                <>
                  <hr />
                  <h3>
                    Model Scoring Started:{" "}
                    <FormatDateTime date={model.scoring_dt} />
                  </h3>
                </>
              )}
              {model.status === "Scored" && (
                <>
                  <hr />
                  <h3>
                    Model Scored: <FormatDateTime date={model.scoring_dt} />
                  </h3>
                </>
              )}
              </div>
          </div>
        </section>
      </div>
      <EditNameAndDescription 
        toggleShowModal={toggleEditNameModal}
        data={{name: model.name, description: model.description}}
        confirmChanges={onUpdateName}
        showModal={editNameModal}
      />
      <ConfirmSuccess show={showSuccess} label="Model updated succesfully." />
      <EfficacyWarningMessage showModal={showEfficacyWarning} toggleModal={toggleShowEfficacyWarning} onContinue={handleSubmitScore} />
      <EfficacyErrorMessage showModal={showEfficacyError} toggleModal={toggleShowEfficacyError} />
      <EfficacyLowErrorMessage showModal={showLowEfficacyError} toggleModal={toggleShowLowEfficacyError} />
    </div>
  );
}

/**
 * show error message when the model efficacy is too high
 * @param {String} showModal pass 'true/false' to show error message.
 * @param {Function} toggleModal pass function to 'show/hide the modal.
 */
const EfficacyErrorMessage = ({showModal, toggleModal}) => {
  return (
    <Modal centered backdrop isOpen={showModal}>
      <ModalHeader>
        <i className="bi bi-exclamation-octagon-fill"/> Stop
      </ModalHeader>
      <ModalBody>
        The unusually strong model metrics indicate something unexpected is occurring. This can occur if the model was built on a population selected using the same variables on which the model was trained or if it was created by combining multiple models. It is not recommended that you move forward with this model. Please contact AudienceMaker Customer Success for assistance.
      </ModalBody>
      <ModalFooter>
        <Button className="promote" onClick={toggleModal}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}

/**
 * show warning message when the model efficacy is high
 * @param {String} showModal pass 'true/false' to show error message.
 * @param {Function} onContinue pass the function to run when user clicks continue: on warning message only.
 * @param {Function} toggleModal pass function to 'show/hide the modal.
 */
const EfficacyWarningMessage = ({showModal, toggleModal, onContinue}) => {
  return (
    <Modal centered backdrop isOpen={showModal}>
      <ModalHeader>
        <i className="bi bi-exclamation-triangle-fill"/> Alert 
      </ModalHeader>
      <ModalBody>
        This model appears to be unusually strong, which might indicate an issue. This can occur if the model was built on a population selected using the same variables on which the model was trained or if it was created by combining multiple models. Please proceed with caution, as using this model could lead to fewer bins than expected or less effective results. If you have questions, please contact AudienceMaker Customer Success.
      </ModalBody>
      <ModalFooter>
        <Button onClick={toggleModal}>Back to Model</Button>
        <Button className="promote" onClick={onContinue}>Continue</Button>
      </ModalFooter>
    </Modal>
  )
}


/**
 * show warning message when the model efficacy is high
 * @param {String} showModal pass 'true/false' to show error message.
 * @param {Function} onContinue pass the function to run when user clicks continue: on warning message only.
 * @param {Function} toggleModal pass function to 'show/hide the modal.
 */
const EfficacyLowErrorMessage = ({showModal, toggleModal}) => {
  return (
    <Modal centered backdrop isOpen={showModal}>
      <ModalHeader>
        <i className="bi bi-exclamation-triangle-fill"/> Alert 
      </ModalHeader>
      <ModalBody>
      Stop: The unusually weak model metrics indicate something unexpected is occurring. It is not recommended that you move forward with this model. Please contact AudienceMaker Customer Success for assistance.
      </ModalBody>
      <ModalFooter>
        <Button className="promote" onClick={toggleModal}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}